import api from './api';
import { PurchaseOrder, SupplierPayment, SupplierDebtSummary } from '@/types';

export interface PurchaseOrderItemCreate {
  product_id?: number;
  custom_name?: string;
  quantity_ordered: number;
  unit_cost: number;
  discount_percentage?: number;
  notes?: string;
}

export interface PurchaseOrderCreate {
  supplier_id?: number;
  order_type: 'standard' | 'debt_only';
  order_date?: string;
  description?: string;
  notes?: string;
  total_amount?: number;
  discount_percentage?: number;
  tax_percentage?: number;
  extra_tax_label?: string | null;
  extra_tax_percentage?: number;
  items: PurchaseOrderItemCreate[];
}

export interface PurchaseOrderUpdate {
  supplier_id?: number;
  order_date?: string;
  description?: string;
  notes?: string;
  status?: string;
}

export interface PurchaseOrderFilters {
  supplier_id?: number;
  status?: string;
  skip?: number;
  limit?: number;
}

export interface ReceiveItemData {
  item_id: number;
  quantity_received: number;
}

export interface ReceiveRequest {
  items: ReceiveItemData[];
  notes?: string;
}

// Movimientos de la cuenta corriente del proveedor
//   payment     -> pago real (baja la deuda y sale plata de la caja)
//   credit_note -> nota de crédito (baja la deuda, no mueve plata)
//   debit_note  -> nota de débito (sube la deuda)
export type SupplierMovementType = 'payment' | 'credit_note' | 'debit_note';

export const SUPPLIER_MOVEMENT_LABELS: Record<SupplierMovementType, string> = {
  payment: 'Pago',
  credit_note: 'Nota de crédito',
  debit_note: 'Nota de débito',
};

export interface SupplierPaymentCreate {
  amount: number;
  movement_type?: SupplierMovementType;
  payment_method: string;
  payment_date?: string;
  reference?: string;
  notes?: string;
  deduct_from_register?: boolean;
}

export const purchaseOrdersService = {
  async getPurchaseOrders(filters?: PurchaseOrderFilters): Promise<PurchaseOrder[]> {
    const response = await api.get<PurchaseOrder[]>('/purchase-orders', { params: filters });
    return response.data;
  },

  async getPurchaseOrder(id: number): Promise<PurchaseOrder> {
    const response = await api.get<PurchaseOrder>(`/purchase-orders/${id}`);
    return response.data;
  },

  async createPurchaseOrder(data: PurchaseOrderCreate): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>('/purchase-orders', data);
    return response.data;
  },

  async updatePurchaseOrder(id: number, data: PurchaseOrderUpdate): Promise<PurchaseOrder> {
    const response = await api.put<PurchaseOrder>(`/purchase-orders/${id}`, data);
    return response.data;
  },

  async cancelPurchaseOrder(id: number): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${id}/cancel`);
    return response.data;
  },

  async deletePurchaseOrder(id: number): Promise<void> {
    await api.delete(`/purchase-orders/${id}`);
  },

  async receiveItems(poId: number, data: ReceiveRequest): Promise<PurchaseOrder> {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${poId}/receive`, data);
    return response.data;
  },

  async registerSupplierPayment(supplierId: number, data: SupplierPaymentCreate): Promise<SupplierPayment> {
    const response = await api.post<SupplierPayment>(`/purchase-orders/supplier/${supplierId}/pay`, data);
    return response.data;
  },

  async getSupplierPayments(supplierId: number): Promise<SupplierPayment[]> {
    const response = await api.get<SupplierPayment[]>(`/purchase-orders/supplier/${supplierId}/payments`);
    return response.data;
  },

  async deleteSupplierPayment(paymentId: number): Promise<void> {
    await api.delete(`/purchase-orders/payments/${paymentId}`);
  },

  async getDebtSummary(): Promise<SupplierDebtSummary[]> {
    const response = await api.get<SupplierDebtSummary[]>('/purchase-orders/debt-summary');
    return response.data;
  },
};
